import { PopularOption } from '@/entities/map'
import { AdditionalOption, BasicOption, RoomIcon } from '@/entities/room'
import type { RoomIconName } from '@/entities/room'
import { useMultiScrollState } from '@/shared/lib'

import { FilterChip } from './filter-chip'
import { ExpandButton, useExpand } from '../../lib/expand'
import { Filter, useFilter } from '../../model/filter'

type Option<T> = {
  title: string
  value: T
  icon: RoomIconName
}

const POPULAR_OPTIONS: Option<PopularOption>[] = [
  {
    title: '주차 공간',
    value: PopularOption.PARKING,
    icon: 'parking',
  },
  {
    title: '엘리베이터',
    value: PopularOption.ELEVATOR,
    icon: 'elevator',
  },
  {
    title: '반려동물 동반',
    value: PopularOption.PET_ALLOWED,
    icon: 'petAllowed',
  },
  {
    title: '복층',
    value: PopularOption.DUPLEX_STRUCTURE,
    icon: 'duplexStructure',
  },
]

const BASIC_OPTIONS: Option<BasicOption>[] = [
  {
    title: '냉장고',
    value: BasicOption.REFRIGERATOR,
    icon: 'refrigerator',
  },
  {
    title: '세탁기',
    value: BasicOption.WASHING_MACHINE,
    icon: 'washingMachine',
  },

  {
    title: '에어컨',
    value: BasicOption.AIR_CONDITIONER,
    icon: 'airConditioner',
  },
  {
    title: 'TV',
    value: BasicOption.TV,
    icon: 'tv',
  },
  {
    title: '와이파이',
    value: BasicOption.WIFI,
    icon: 'wifi',
  },
  {
    title: '싱크대',
    value: BasicOption.SINK,
    icon: 'sink',
  },
  {
    title: '가스레인지',
    value: BasicOption.GAS_STOVE,
    icon: 'gasStove',
  },
  {
    title: '인덕션',
    value: BasicOption.INDUCTION,
    icon: 'induction',
  },

  {
    title: '침대',
    value: BasicOption.BED,
    icon: 'bed',
  },
]

const ADDITIONAL_OPTIONS: Option<AdditionalOption>[] = [
  {
    title: '도어락',
    value: AdditionalOption.DOOR_LOCK,
    icon: 'doorlock',
  },
  {
    title: 'CCTV',
    value: AdditionalOption.CCTV,
    icon: 'cctv',
  },

  {
    title: '관리실',
    value: AdditionalOption.MANAGEMENT_OFFICE,
    icon: 'managementOffice',
  },

  {
    title: '식탁',
    value: AdditionalOption.DINING_TABLE,
    icon: 'diningTable',
  },
  {
    title: '전자레인지',
    value: AdditionalOption.MICROWAVE,
    icon: 'microwave',
  },
  {
    title: '전기밥솥',
    value: AdditionalOption.RICE_COOKER,
    icon: 'riceCooker',
  },
  {
    title: '정수기',
    value: AdditionalOption.WATER_PURIFIER,
    icon: 'waterPurifier',
  },
  {
    title: '소파',
    value: AdditionalOption.SOFA,
    icon: 'sofa',
  },
  {
    title: '책상',
    value: AdditionalOption.DESK,
    icon: 'desk',
  },
  {
    title: '옷장',
    value: AdditionalOption.CLOSET,
    icon: 'closet',
  },
  {
    title: '신발장',
    value: AdditionalOption.SHOE_RACK,
    icon: 'shoeRack',
  },
  {
    title: '화장대',
    value: AdditionalOption.VANITY,
    icon: 'vanity',
  },

  {
    title: '욕조',
    value: AdditionalOption.BATHTUB,
    icon: 'bathtub',
  },
  {
    title: '비데',
    value: AdditionalOption.BIDET,
    icon: 'bidet',
  },
  {
    title: '건조기',
    value: AdditionalOption.DRYER,
    icon: 'dryer',
  },

  {
    title: '발코니/베란다',
    value: AdditionalOption.BALCONY,
    icon: 'balcony',
  },
  {
    title: '드레스룸',
    value: AdditionalOption.DRESSING_ROOM,
    icon: 'dressingRoom',
  },
  {
    title: '케이블 TV',
    value: AdditionalOption.CABLE_TV,
    icon: 'cableTV',
  },
  {
    title: '커튼',
    value: AdditionalOption.CURTAINS,
    icon: 'curtain',
  },
  {
    title: '공기청정기',
    value: AdditionalOption.AIR_PURIFIER,
    icon: 'airPurifier',
  },
]

export const Options = () => {
  const { target, registerScrollElement, scrollIntoView } =
    useMultiScrollState<HTMLDivElement>()

  const { expanded, toggleExpand } = useExpand({
    defaultExpanded: target === Filter.Options,
  })

  const {
    filter: { popularOptions, basicOptions, additionalOptions },
    updateFilter,
  } = useFilter()

  const BASIC_OPTIONS_LIST = expanded
    ? BASIC_OPTIONS
    : BASIC_OPTIONS.slice(0, 6)

  const handleExpandButtonClick = () => {
    toggleExpand()

    requestAnimationFrame(() => {
      scrollIntoView(Filter.Options, {
        behavior: 'smooth',
        block: 'start',
      })
    })
  }

  const handlePopularOptionChange = (value: PopularOption) => () => {
    updateFilter({ popularOptions: value })
  }

  const handleBasicOptionChange = (value: BasicOption) => () => {
    updateFilter({ basicOptions: value })
  }

  const handleAdditionalOptionChange = (value: AdditionalOption) => () => {
    updateFilter({ additionalOptions: value })
  }

  return (
    <div
      className="space-y-3 p-6 pt-0 pr-4"
      ref={element => {
        if (element) {
          registerScrollElement(Filter.Options, element)
        }
      }}
    >
      <h3 className="typo-body1 font-semibold text-black">옵션</h3>
      <div>
        <h4 className="typo-label1 mb-2 font-semibold text-purple-500">인기</h4>
        <ul className="flex flex-wrap gap-2">
          {POPULAR_OPTIONS.map(({ title, value, icon }) => (
            <li key={title}>
              <FilterChip
                selected={popularOptions.includes(value)}
                onClick={handlePopularOptionChange(value)}
                className="flex pl-3"
                startIcon={<RoomIcon name={icon} size={20} />}
              >
                <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                  {title}
                </p>
              </FilterChip>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="typo-label1 mb-2 font-semibold text-purple-500">기본</h4>
        <ul className="flex flex-wrap gap-2">
          {BASIC_OPTIONS_LIST.map(({ title, value, icon }) => (
            <li key={title}>
              <FilterChip
                selected={basicOptions.includes(value)}
                onClick={handleBasicOptionChange(value)}
                className="flex pl-3"
                startIcon={<RoomIcon name={icon} size={20} />}
              >
                <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                  {title}
                </p>
              </FilterChip>
            </li>
          ))}
        </ul>
      </div>
      {expanded && (
        <div>
          <h4 className="typo-label1 mb-2 font-semibold text-purple-500">
            추가
          </h4>
          <ul className="flex flex-wrap gap-2">
            {ADDITIONAL_OPTIONS.map(({ title, value, icon }) => (
              <li key={title}>
                <FilterChip
                  selected={additionalOptions.includes(value)}
                  onClick={handleAdditionalOptionChange(value)}
                  className="flex pl-3"
                  startIcon={<RoomIcon name={icon} size={20} />}
                >
                  <p className="ml-1 flex h-11 items-center justify-center whitespace-break-spaces">
                    {title}
                  </p>
                </FilterChip>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex justify-center">
        <ExpandButton expanded={expanded} onClick={handleExpandButtonClick} />
      </div>
    </div>
  )
}
